import { Plus } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, getErrorMessage } from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";
import { useTenant } from "../contexts/TenantContext.tsx";
import SearchField from "./SearchField.tsx";
import CenteredSpinner from "./CenteredSpinner.tsx";

interface ListSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (listId: string, listName: string) => void;
  /** Tenant to source lists from. Falls back to the globally selected tenant. */
  tenantId?: string;
}

export default function ListSelector({
  open,
  onClose,
  onSelect,
  tenantId: propTenantId,
}: ListSelectorProps) {
  const { selectedTenantId } = useTenant();
  const tenantId = propTenantId ?? selectedTenantId;
  const queryClient = useQueryClient();
  const [newListName, setNewListName] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const lists = useQuery({
    queryKey: keys.prospectList.list(tenantId ?? ""),
    queryFn: () => api.getProspectLists(tenantId!),
    enabled: open && !!tenantId,
  });

  const createList = useMutation({
    mutationFn: (name: string) =>
      api.createProspectList({ tenant_id: tenantId!, name }),
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      setNewListName("");
      setError(null);
      onSelect(newList.id, newList.name);
    },
    onError: (err) => setError(getErrorMessage(err)),
    meta: { suppressErrorToast: true },
  });

  const filtered = (lists.data ?? []).filter(
    (l) => !search || l.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleCreate = () => {
    const name = newListName.trim();
    if (!name) return;
    createList.mutate(name);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Select collection</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 1 }}>
          <SearchField value={search} onChange={setSearch} placeholder="Search collections" fullWidth autoFocus clearable />
        </Box>

        {lists.isLoading && (
          <CenteredSpinner py={2} size={24} />
        )}

        {filtered.length > 0 && (
          <List dense sx={{ maxHeight: 240, overflow: "auto" }}>
            {filtered.map((l) => (
              <ListItemButton
                key={l.id}
                onClick={() => onSelect(l.id, l.name)}
              >
                <ListItemText
                  primary={l.name}
                  secondary={`${l.prospect_count} prospects`}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {lists.data && filtered.length === 0 && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              py: 1
            }}>
            {search ? "No matching collections." : "No collections yet."}
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="New collection name..."
            value={newListName}
            onChange={(e) => {
              setNewListName(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
            fullWidth
            error={!!error}
            helperText={error}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus />}
            onClick={handleCreate}
            disabled={!newListName.trim() || createList.isPending}
            sx={{ whiteSpace: "nowrap" }}
          >
            Create
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
